export type Environment = 'development' | 'production' | 'local';

interface EnvironmentConfig {
  development: string;
  production: string;
  local: string;
}

const SERVER_URLS: EnvironmentConfig = {
  development: 'https://api.gather-community.com/dev/api',
  production: 'https://api.gather-community.com/api',
  local: 'http://localhost:3000',
};

const STORAGE_KEY = 'gather.admin.env';
const DEFAULT_ENV: Environment = 'production';

// Lowercase aliases so /admin?env=dev keeps working alongside the full names.
const ALIAS_MAP: Record<string, Environment> = {
  dev: 'development',
  development: 'development',
  prod: 'production',
  production: 'production',
  local: 'local',
};

const normalize = (value: string | null | undefined): Environment | null => {
  if (!value) return null;
  return ALIAS_MAP[value.toLowerCase()] ?? null;
};

export const getCurrentEnvironment = (): Environment => {
  if (typeof window === 'undefined') return DEFAULT_ENV;

  // ?env=... is a one-time override that persists to storage so subsequent
  // page loads (without the query param) stick to the chosen environment.
  const params = new URLSearchParams(window.location.search);
  const fromUrl = normalize(params.get('env'));
  if (fromUrl) {
    try {
      localStorage.setItem(STORAGE_KEY, fromUrl);
    } catch {
      // ignore storage failures (private mode, etc.)
    }
    return fromUrl;
  }

  try {
    const stored = normalize(localStorage.getItem(STORAGE_KEY));
    if (stored) return stored;
  } catch {
    // ignore
  }

  return DEFAULT_ENV;
};

export const setCurrentEnvironment = (env: Environment): void => {
  try {
    localStorage.setItem(STORAGE_KEY, env);
  } catch {
    // ignore
  }
};

export const getBaseUrl = (): string =>
  SERVER_URLS[getCurrentEnvironment()];

export const getEnvironmentOptions = (): Array<{
  label: string;
  value: Environment;
}> => [
  { label: 'Production', value: 'production' },
  { label: 'Development', value: 'development' },
  { label: 'Local', value: 'local' },
];
